import * as React from 'react'
import styled from 'styled-components'
import { IMeetupPlace, IWindow } from 'static/interfaces'
import { otaReservationService } from 'core/services'
import { Button, CheckBox, InputText, InputTextArea } from 'components/atoms'
import { IBuildedTask, IBuildedTour, IBuildedTourGroup } from 'utils/weeklyTypes'
import { IConnectionMailTemplate } from 'static/interfaces'
import Typography from 'components/atoms/_layouts/Typography'
import { COLORS } from 'static/constants'
import { updatedTourByOtaReservation } from 'utils/weekly'
import ReactSelect from 'react-select'
import { ConnectionMessagePreviewModalBody } from './ConnectionMessagePreviewModalBody'
import ExpandSection from 'components/molecules/ExpandSection'
import { replaceEmptyToNull } from 'utils/form'

declare var window: IWindow

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${COLORS.primary}` },
  }),
  menu: (provided, state) => ({
    ...provided,
    width: 'calc(100% - 8px)',
    marginLeft: '4px',
    marginRight: '4px',
  }),
}

interface IProps extends IBuildedTourGroup {
  selectedTour: IBuildedTour
  onTourUpdate: (tour: IBuildedTour) => void
  meetupPlaces: IMeetupPlace[]
  connectionMailTemplates: IConnectionMailTemplate[]
}

interface IMeetupPlaceOption {
  value: string
  label: string
}

interface ICustomMeetupPlace {
  custom_meetup_point: string | null
  custom_meetup_point_url: string | null
}

const generateMeetupPlaceLabel = ({ name_en, name_ja }: IMeetupPlace): string => {
  return `${name_en} | ${name_ja}`
}

export const ConnectTodoModalBody = (props: IProps) => {
  const { selectedTour, onTourUpdate } = props

  const otaReservation = React.useMemo(() => {
    return props.assignedOtaReservations.length > 0 ? props.assignedOtaReservations[0] : null
  }, [props.assignedOtaReservations])

  const meetupPlaceOptions = React.useMemo(() => {
    return [
      {
        value: '',
        label: '選択する',
      },
      ...props.meetupPlaces.map(p => ({ value: String(p.id), label: generateMeetupPlaceLabel(p) })),
    ]
  }, [props.meetupPlaces])

  const [meetupPlace, setMeetupPlace] = React.useState<IMeetupPlace | null>(
    otaReservation.meetup_place
  )
  const [customMeetupPlace, setCustomMeetupPlace] = React.useState<ICustomMeetupPlace>({
    custom_meetup_point: otaReservation.custom_meetup_point ?? null,
    custom_meetup_point_url: otaReservation.custom_meetup_point_url ?? null,
  })

  const [doneTasks, setDoneTasks] = React.useState<IBuildedTask[]>(props.tasks.filter(t => t.done))
  const [connectionMailTemplateId, setConnectionMailTemplateId] = React.useState<number | null>(
    otaReservation.connection_mail_template_id
  )
  const hasTextValue = React.useCallback((value: string | null | undefined): boolean => {
    return typeof value === 'string' && value.trim() !== ''
  }, [])

  const hasCustomMeetupInput = React.useMemo(() => {
    return (
      hasTextValue(customMeetupPlace.custom_meetup_point) ||
      hasTextValue(customMeetupPlace.custom_meetup_point_url)
    )
  }, [
    customMeetupPlace.custom_meetup_point,
    customMeetupPlace.custom_meetup_point_url,
    hasTextValue,
  ])

  const hasSelectedMeetupPlace = React.useMemo(() => {
    return meetupPlace !== null && meetupPlace !== undefined
  }, [meetupPlace])

  const shouldDisableMeetupSelect = hasCustomMeetupInput
  const shouldDisableCustomMeetupInputs = !hasCustomMeetupInput && hasSelectedMeetupPlace

  const onChangeMeetupPlace = (option: IMeetupPlaceOption | null) => {
    if (option) {
      setMeetupPlace(props.meetupPlaces.find(p => String(p.id) === String(option.value)) ?? null)
    } else {
      setMeetupPlace(null)
    }
  }

  const onChangeCustomMeetupPlace = ({ place, url }) => {
    setCustomMeetupPlace({
      custom_meetup_point: place,
      custom_meetup_point_url: url,
    })
  }

  const submit = async () => {
    const customMeetupPoint = replaceEmptyToNull(customMeetupPlace?.custom_meetup_point)
    const customMeetupPointUrl = replaceEmptyToNull(customMeetupPlace?.custom_meetup_point_url)

    const meetupPlaceIdForSubmit = meetupPlace && !hasCustomMeetupInput ? meetupPlace.id : null
    const customMeetupPointForSubmit = hasCustomMeetupInput ? customMeetupPoint : null
    const customMeetupPointUrlForSubmit = hasCustomMeetupInput ? customMeetupPointUrl : null

    try {
      const { updatedOtaReservation, flush } = await otaReservationService.update(
        otaReservation.id,
        {
          task_ids: doneTasks.map(({ task_id }) => {
            return task_id
          }),
          meetup_place_id: meetupPlaceIdForSubmit,
          custom_meetup_point: customMeetupPointForSubmit,
          custom_meetup_point_url: customMeetupPointUrlForSubmit,
          connection_mail_template_id: connectionMailTemplateId,
        }
      )

      let newTour = selectedTour
      newTour = updatedTourByOtaReservation(newTour, updatedOtaReservation.id, {
        ota_reservation_tasks: updatedOtaReservation.ota_reservation_tasks,
        meetup_place: updatedOtaReservation.meetup_place,
        custom_meetup_point: updatedOtaReservation.custom_meetup_point,
        custom_meetup_point_url: updatedOtaReservation.custom_meetup_point_url,
        connection_mail_template_id: updatedOtaReservation.connection_mail_template_id,
      })
      onTourUpdate(newTour)

      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      window.globalModal.closeModal()
    } catch (e) {
      console.error(e)
    } finally {
      window.globalModal.closeModal()
    }
  }

  const hasMeetupPlace = React.useMemo(() => {
    return hasSelectedMeetupPlace || hasCustomMeetupInput
  }, [hasCustomMeetupInput, hasSelectedMeetupPlace])

  React.useEffect(() => {
    if (!hasMeetupPlace) {
      const hotelTask = props.tasks.find(task => task.label === 'ホ')
      if (!hotelTask) {
        return
      }

      setDoneTasks(prevDoneTasks => {
        const isAlreadyDone = prevDoneTasks.some(t => t.id === hotelTask.id)
        if (isAlreadyDone) {
          return prevDoneTasks.filter(t => t.id !== hotelTask.id)
        }
        return prevDoneTasks
      })
    }
  }, [hasMeetupPlace, props.tasks])

  // 検索用の事前キャッシュマップ
  const meetupPlaceMap = React.useMemo(() => {
    const map = new Map()
    props.meetupPlaces.forEach(place => {
      map.set(place.id, {
        sub_area: place.sub_area?.toLowerCase() || '',
        name_ja: place.name_ja?.toLowerCase() || '',
        name_en: place.name_en?.toLowerCase() || '',
      })
    })
    return map
  }, [props.meetupPlaces])

  const customFilterOption = React.useCallback(
    (option: any, searchText: string) => {
      if (!searchText) {
        return true
      }

      const targetPlace = meetupPlaceMap.get(Number(option.value))
      if (!targetPlace) {
        return false
      }

      const separated = searchText
        .toLowerCase()
        .split(/[\s　]+/)
        .filter(k => k.trim() !== '')

      return separated.every(query => {
        return (
          targetPlace.sub_area.includes(query) ||
          targetPlace.name_ja.includes(query) ||
          targetPlace.name_en.includes(query)
        )
      })
    },
    [meetupPlaceMap]
  )

  return (
    <div>
      <ConnectTodoModalBodyInner>
        {!props.connected &&
          props.tasks.map(task => {
            const isHotelTask = task.label === 'ホ'
            const isTaskDone = doneTasks.some(t => t.id === task.id)
            return (
              <div className="checkbox_wrap" key={task.id}>
                <CheckBox
                  label={task.name}
                  name={task.name}
                  value={task.id}
                  checked={!isTaskDone}
                  defaultChecked={!task.done}
                  showLabelInline={true}
                  disabled={task.label === 'ホ' && !hasMeetupPlace}
                  onChangeHandler={e => {
                    e.persist()
                    if (isHotelTask && !hasMeetupPlace) {
                      return
                    }
                    setDoneTasks(old => {
                      return e.target.checked
                        ? old.filter(t => t.id !== task.id)
                        : [...old, { ...task, done: true }]
                    })
                  }}
                />
                {task.label === 'ホ' && !hasMeetupPlace && (
                  <Typography type="subtitle" color="subtext">
                    集合場所が設定されていません
                  </Typography>
                )}
              </div>
            )
          })}
        <div>
          <div className="label">
            <Typography type="subtitle" size="lg">
              Meetup Place
            </Typography>
            <Typography type="body" color="subtext">
              Selectまたは自由入力で集合場所を設定してください。
            </Typography>
          </div>

          <ReactSelect
            placeholder="Meetup Placeを選択"
            options={meetupPlaceOptions}
            isMulti={false}
            isDisabled={shouldDisableMeetupSelect}
            filterOption={customFilterOption}
            onChange={onChangeMeetupPlace}
            styles={selectCustomStyles}
            defaultValue={
              otaReservation.meetup_place
                ? {
                    value: otaReservation.meetup_place.id,
                    label: generateMeetupPlaceLabel(otaReservation.meetup_place),
                  }
                : null
            }
            // menuPortalTarget={document.querySelector('body')}
          />
        </div>
        <div>
          <div className="label">
            <Typography type="subtitle" size="lg">
              Meetup Place（自由入力）
            </Typography>
          </div>

          <InputTextArea
            name="meetup_place"
            placeholder="Meetup Placeを入力してください"
            disabled={shouldDisableCustomMeetupInputs}
            defaultValue={otaReservation.custom_meetup_point ?? ''}
            onChangeHandler={e => {
              onChangeCustomMeetupPlace({
                place: e.target.value,
                url: customMeetupPlace?.custom_meetup_point_url ?? null,
              })
            }}
            onBlurHandler={e => {
              onChangeCustomMeetupPlace({
                place: e.target.value,
                url: customMeetupPlace?.custom_meetup_point_url ?? null,
              })
            }}
          />
          <InputText
            name="meetup_place_url"
            placeholder="Meetup PlaceのURLを入力してください"
            disabled={shouldDisableCustomMeetupInputs}
            defaultValue={otaReservation.custom_meetup_point_url ?? ''}
            onChangeHandler={e => {
              onChangeCustomMeetupPlace({
                place: customMeetupPlace?.custom_meetup_point ?? null,
                url: e.target.value,
              })
            }}
            onBlurHandler={e => {
              onChangeCustomMeetupPlace({
                place: customMeetupPlace?.custom_meetup_point ?? null,
                url: e.target.value,
              })
            }}
          />
        </div>
        {props.connectionMailTemplates.length > 0 && (
          <div>
            <div className="label">
              <Typography type="subtitle" size="lg">
                Mail Template
              </Typography>
            </div>
            <ReactSelect
              options={props.connectionMailTemplates.map(mailTemplate => ({
                value: mailTemplate.id,
                label: mailTemplate.title,
              }))}
              maxMenuHeight={160}
              value={{
                value: connectionMailTemplateId,
                label: props.connectionMailTemplates.find(
                  mailTemplate => mailTemplate.id === connectionMailTemplateId
                )?.title,
              }}
              onChange={option => setConnectionMailTemplateId(option.value)}
            />
          </div>
        )}
        <div>
          <Typography type="subtitle" size="lg">
            その他の内容（エージェント限定メモ）
          </Typography>
          <div className="other_description">{props.internal_memo ?? '記載がありません'}</div>
        </div>
        <div className="preview_wrapper">
          {props.assignedOtaReservations.length > 0 && (
            <ExpandSection title="つなぐ確認メッセージのプレビュー" defaultOpen={false}>
              <ConnectionMessagePreviewModalBody
                id={props.assignedOtaReservations[0].id}
                connectionMailTemplateId={connectionMailTemplateId}
              />
            </ExpandSection>
          )}
        </div>
        {!props.connected && (
          <Button primary={true} handleClick={submit}>
            保存
          </Button>
        )}
      </ConnectTodoModalBodyInner>
    </div>
  )
}

const ConnectTodoModalBodyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .preview_wrapper {
    margin-top: 32px;
  }

  .checkbox_wrap {
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 16px;
  }
  textarea {
    height: 80px;
    margin-bottom: 4px;
  }

  .label {
    margin-bottom: 4px;
  }

  .other_description {
    word-break: break-word;
    white-space: pre-wrap;
    font-size: 14px;
    margin: 4px 0;
  }
`
